// src/pages/ProjectInviteAccept.tsx
import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useAuthRedirect } from "@/hooks/useAuthRedirect";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { projectApi } from "@/services/projectService";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { ProjectInvitationResponse } from "@/types/invitation";
import { AxiosError } from "axios";

const ProjectInviteAccept = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { getLoginUrlWithRedirect } = useAuthRedirect();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);

  // Get token from URL query params
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  // Validate the invitation
  const {
    data: validation,
    isLoading,
    isError,
    error,
  } = useQuery<
    ProjectInvitationResponse,
    AxiosError<{ isValid: boolean; error: string }>
  >({
    queryKey: ["projectInviteValidation", token],
    queryFn: async (): Promise<ProjectInvitationResponse> => {
      const response = await projectApi.validateProjectInvite(token);
      return {
        type: "project",
        isValid: response.isValid,
        invitationId: response.invitationId,
        email: response.email,
        role: response.role,
        project: response.project,
        projectId: response.projectId,
        invitedBy: response.invitedBy,
      };
    },
    enabled: !!token,
    staleTime: 1000,
  });

  // Accept invitation mutation
  const acceptMutation = useMutation({
    mutationFn: projectApi.acceptProjectInvite,
    onSuccess: (data) => {
      toast({
        title: "Success",
        description: data.message || "Invitation accepted successfully",
        variant: "default",
      });
      // Navigate to projects page instead of specific project since unified invitations might have multiple projects
      navigate(`/projects`);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error: AxiosError<{ error: string }>) => {
      toast({
        title: "Error",
        description: error?.response?.data?.error || error?.message || "Failed to accept invitation",
        variant: "destructive",
      });
    },
  });

  // Handle accept button click
  const handleAcceptInvitation = async () => {
    if (!token) return;
    setIsProcessing(true);
    try {
      await acceptMutation.mutateAsync(token);
    } finally {
      setIsProcessing(false);
    }
  };

  // Redirect if no token
  useEffect(() => {
    if (!token) {
      toast({
        title: "Invalid invitation",
        description: "No invitation token was provided",
        variant: "destructive",
      });
      navigate("/projects");
    }
  }, [token, navigate, toast]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Validating Invitation</CardTitle>
            <CardDescription>
              Please wait while we validate your project invitation...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state
  if (isError || !validation?.isValid) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md border-destructive">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">
              Invalid Invitation
            </CardTitle>
            <CardDescription>
              {error?.response?.data?.error || error?.message || "Invitation is invalid or has expired"}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            <Button variant="outline" onClick={() => navigate("/projects")}>
              Back to Projects
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show invitation details
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Project Invitation</CardTitle>
          <CardDescription>
            You've been invited to join a project
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h3 className="font-medium">Project:</h3>
            <p className="text-lg font-semibold">{validation.project?.name}</p>
            <p className="text-sm text-muted-foreground">
              {validation.project?.address}, {validation.project?.city},{" "}
              {validation.project?.state}
            </p>
          </div>

          <div className="space-y-2">
            <h3 className="font-medium">Role:</h3>
            <p className="text-lg font-semibold">{validation.role}</p>
          </div>

          <div className="space-y-2">
            <h3 className="font-medium">Invited By:</h3>
            <p className="text-lg font-semibold">
              {validation.invitedBy?.name}
            </p>
            <p className="text-sm text-muted-foreground">
              {validation.invitedBy?.email}
            </p>
          </div>

          {!isAuthenticated && (
            <div className="p-4 bg-yellow-50 rounded-md border border-yellow-200">
              <p className="text-yellow-800">
                You need to sign in first to accept this invitation.
              </p>
              <Button
                variant="outline"
                className="mt-2 w-full"
                onClick={() => navigate(getLoginUrlWithRedirect())}
              >
                Sign In
              </Button>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={() => navigate("/projects")}>
            Decline
          </Button>
          <Button
            onClick={handleAcceptInvitation}
            disabled={
              !isAuthenticated || isProcessing || acceptMutation.isPending
            }
          >
            {isProcessing || acceptMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Accept Invitation"
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ProjectInviteAccept;
